import React, { ComponentType, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { MuiContainer, MuiTypography, MuiBox } from 'theme/material-ui';
import Spacer from 'components/UI/Layout/Spacer';
import Footer from 'components/common/Footer';
import * as covidScreeningSelectors from 'store/CovidScreening/selectors';
import { RootState } from 'store/types';
import { Patient } from 'store/CovidScreening/types';
import { patientSelect } from 'modules/constants/covidScreening';
import { covidTestingFacilities, myHealthPlus } from 'modules/constants/urls';
import { Color } from 'shared/src/modules/styles/colors';
import { CovidTestButton } from '../../CovidScreening/Components/CovidTestButton';
import { AnalyticsEvent } from 'services/AnalyticsService';
import { CovidTitles } from 'lib/constants/covidScreening';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';
import { Container, StyledImage } from './styled';
import People from 'assets/Peopels.png';
import { FontSize, FontWeight } from 'modules/styles/variables';

export interface Props extends RouteComponentProps {
  patientsData: Patient;
}

const healthPlusAccPerks = [
  {
    title: 'Personalized care',
    desc: 'See all your health info at a glance.'
  },
  {
    title: 'Notifications',
    desc:
      'Receive alerts about new medical info available, new messages from your provider, and more.'
  },
  {
    title: 'Access to your doctors',
    desc: 'Connect with your past and preferred providers.'
  }
];

export function CovidConfirmation({ history, patientsData }: Props) {
  const facilitiesLink = useNavigationAnalytics(AnalyticsEvent.CovidFacilitiesLinkClicked);
  const covidOrderConfirmation = useNavigationAnalytics(AnalyticsEvent.CovidOrderCompleted);
  const covidScreeningClosed = useNavigationAnalytics(AnalyticsEvent.CovidScreeningClosed);
  const handleClose = () => {
    covidScreeningClosed({ title: CovidTitles.CONFIRMATION_PAGE });
    history.push('/guest-home');
  };
  const handleCreateAccount = () => {
    window.open(myHealthPlus);
  };
  const onFacilitiesLinkClick = () => {
    facilitiesLink({
      title: CovidTitles.CONFIRMATION_PAGE
    });
    window.open(covidTestingFacilities, '_blank');
  };

  useEffect(() => {
    covidOrderConfirmation({
      /* eslint-disable @typescript-eslint/camelcase */
      covid19_order: 'Success',
      title: CovidTitles.CONFIRMATION_PAGE
    });
    window.history.pushState(null, document.title, window.location.href);
  }, []);
  window.onpopstate = () => handleClose();
  return (
    <>
      <MuiContainer maxWidth="lg">
        <Spacer size="medium" />
        <MuiBox my={2}>
          <MuiTypography variant="h4" align="center">
            Your COVID-19 test has been ordered.
          </MuiTypography>
        </MuiBox>
        <Spacer size="medium" />
        <MuiBox my={2}>
          <MuiTypography align="justify">
            Please click on the link below to review a list of Intermountain Healthcare facilities
            where you can pick up your saliva (drool) kit and drop off your saliva (drool) sample.
          </MuiTypography>
          <Spacer size="small" />
          <MuiTypography
            display="inline"
            onClick={onFacilitiesLinkClick}
            color={Color.primaryUnderlay}
          >
            Intermountain Healthcare COVID-19 testing facilities
          </MuiTypography>
          <Spacer size="small" />
          <MuiTypography align="justify">Proceed to any site to take the test.</MuiTypography>
          <Spacer size="medium" />
          <MuiTypography variant="h6" align="center">
            A summary of this information will
          </MuiTypography>
          <MuiTypography variant="h6" align="center">
            be sent to {patientsData?.emails[0]?.address} shortly.
          </MuiTypography>
        </MuiBox>

        <Footer
          headerText={patientSelect.GET_PATIENTS.HELPLINE.CovidInfo}
          link={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          linkName={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
          helplineHours={patientSelect.GET_PATIENTS.HELPLINE.CovidHelplineHours}
          title={CovidTitles.CONFIRMATION_PAGE}
        />
        <Spacer size="large" />
        <Container>
          <MuiBox maxWidth="465px">
            <StyledImage height={80} width={160} src={People} />
          </MuiBox>
          <Spacer size="medium" />
          <Spacer size="small" />
          <MuiBox maxWidth="70%" alignSelf="center">
            <MuiTypography
              variant="h6"
              align="left"
              fontSize={FontSize.large}
              fontWeight={FontWeight.semibold}
              color={Color.textDark}
            >
              Creating an account gives you these additional benefits.
            </MuiTypography>
            <Spacer size="medium" />
            {healthPlusAccPerks?.map(perk => (
              <>
                <MuiTypography
                  variant="h6"
                  align="left"
                  fontSize={FontSize.base}
                  color={Color.tealOriginal}
                  fontWeight={FontWeight.bold}
                >
                  {perk?.title}
                </MuiTypography>
                <Spacer size="xxsmall" />
                <MuiTypography
                  variant="h6"
                  align="left"
                  fontSize={FontSize.base}
                  color={Color.textLight}
                >
                  {perk?.desc}
                </MuiTypography>
                <Spacer size="small" />
              </>
            ))}
          </MuiBox>
          <MuiBox width="284px" margin="28px auto" alignItems="center">
            <CovidTestButton
              fullWidth
              style={{
                height: 45,
                fontSize: FontSize.base,
                background: Color.tealOriginal,
                fontWeight: FontWeight.normal
              }}
              data-testid="Create-Acc"
              variant="contained"
              onClick={handleCreateAccount}
            >
              Create MyHealth + account
            </CovidTestButton>
          </MuiBox>
        </Container>
        <Spacer size="large" />
        <MuiBox width="284px" margin="0 auto" alignItems="center">
          <CovidTestButton
            fullWidth
            style={{ height: 45, fontSize: FontSize.base, fontWeight: FontWeight.normal }}
            data-testid="Close"
            color="primary"
            variant="contained"
            onClick={handleClose}
          >
            Close
          </CovidTestButton>
        </MuiBox>
        <Spacer size="large" />
      </MuiContainer>
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  patientsData: covidScreeningSelectors.CovidScreeningPatientInfoSelector(state)
});

export default connect(mapStateToProps)(CovidConfirmation as ComponentType);
